import React, { createContext, useContext, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { Tag, message } from 'antd';

export const api = axios.create({ baseURL: '/api/subcontracts' });
api.interceptors.request.use(config => {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
    return config;
});

export const errorText = error => error?.response?.data?.error || error?.message || 'Ошибка запроса';
export const showError = error => message.error(errorText(error));

export const money = v =>
    v === null || v === undefined || v === ''
        ? '—'
        : Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const coefText = v => (v === null || v === undefined || v === '' ? '—' : String(v).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''));
export const qty = v => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 3 });

/** Точное умножение для коэффициента до 20 знаков (Number теряет хвост). */
/* global BigInt */
const parseDecimal = value => {
    let s = String(value ?? '')
        .trim()
        .replace(/\s/g, '')
        .replace(',', '.');
    if (!s) return { digits: 0n, scale: 0 };
    let neg = false;
    if (s[0] === '-') {
        neg = true;
        s = s.slice(1);
    } else if (s[0] === '+') s = s.slice(1);
    if (/e/i.test(s)) {
        const n = Number(s);
        if (!Number.isFinite(n)) return { digits: 0n, scale: 0 };
        s = Math.abs(n).toFixed(20).replace(/\.?0+$/, '') || '0';
        if (n < 0) neg = !neg;
    }
    if (!/^\d+(\.\d+)?$/.test(s)) return { digits: 0n, scale: 0 };
    const [intPart, frac = ''] = s.split('.');
    const digits = BigInt(`${intPart}${frac}` || '0');
    return { digits: neg ? -digits : digits, scale: frac.length };
};

export const mulRound = (a, b, outScale = 2) => {
    const A = parseDecimal(a);
    const B = parseDecimal(b);
    let prod = A.digits * B.digits;
    const scale = A.scale + B.scale;
    const neg = prod < 0n;
    if (neg) prod = -prod;
    let intPart;
    let frac;
    if (scale === 0) {
        intPart = prod.toString();
        frac = '';
    } else {
        const raw = prod.toString().padStart(scale + 1, '0');
        intPart = raw.slice(0, -scale) || '0';
        frac = raw.slice(-scale);
    }
    if (frac.length < outScale) frac = frac.padEnd(outScale, '0');
    else if (frac.length > outScale) {
        let keep = BigInt(frac.slice(0, outScale) || '0');
        if (frac[outScale] >= '5') keep += 1n;
        const keepStr = keep.toString().padStart(outScale, '0');
        if (keepStr.length > outScale) {
            intPart = (BigInt(intPart) + 1n).toString();
            frac = keepStr.slice(-outScale);
        } else frac = keepStr;
    }
    if (outScale === 0) return Number(`${neg ? '-' : ''}${intPart}`);
    return Number(`${neg ? '-' : ''}${intPart}.${frac}`);
};

export const round2 = v => Math.round((Number(v) || 0) * 100) / 100;
export const ruDate = v => (v ? dayjs(v).format('DD.MM.YYYY') : '—');
export const ruDateTime = v => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '—');

export const ACT_STATUS_COLORS = {
    draft: 'default',
    review: 'processing',
    checked: 'cyan',
    approved: 'blue',
    accounting: 'purple',
    signed: 'green',
    closed: 'success'
};
export const PAYMENT_TITLES = { unpaid: 'Не оплачен', partial: 'Частично', paid: 'Оплачен' };
export const PAYMENT_COLORS = { unpaid: 'default', partial: 'orange', paid: 'green' };

export const WORK_STATE = {
    ready: ['Готова', 'green'],
    error: ['Требует уточнения', 'red'],
    closed: ['Закрыта', 'default']
};

export const ActStatusTag = ({ status, meta }) => (
    <Tag color={ACT_STATUS_COLORS[status] || 'default'}>{meta?.act_statuses?.[status] || status}</Tag>
);

export const actFileName = (act, format) => {
    const ext = format === 'pdf' || format === 'pdf-view' ? 'pdf' : format === 'xlsx' ? 'xlsx' : 'docx';
    if (ext === 'docx' || format === 'docx' || format === 'docx-old') {
        const num = String(act.number || 'без_номера').replace(/[\\/:*?"<>|\s]+/g, '_').replace(/-/g, '_');
        const d = act.act_date ? dayjs(act.act_date).format('DD_MM_YYYY') : 'без_даты';
        return `Акт_выполненных_работ_${num}_от_${d}.${ext}`;
    }
    return `Акт_${act.number}.${ext}`;
};

export const downloadAct = async (act, format = 'docx') => {
    try {
        const res = await api.get(`/acts/${act.id}/export`, { params: { format }, responseType: 'blob' });
        const url = URL.createObjectURL(res.data);
        if (format === 'pdf-view') {
            window.open(url, '_blank');
        } else {
            const a = document.createElement('a');
            a.href = url;
            a.download = actFileName(act, format);
            document.body.appendChild(a);
            a.click();
            a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
        showError(error);
    }
};

export const SubContext = createContext({ me: { roles: [] }, meta: {}, subcontractors: [] });
export const useSub = () => useContext(SubContext);

/** Высота области прокрутки таблицы: от её верха до низа окна (offset — шапка таблицы и пагинация). */
export const useFillHeight = (offset = 125, min = 320) => {
    const ref = useRef(null);
    const [height, setHeight] = useState(560);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const calc = () => {
            const rect = el.getBoundingClientRect();
            if (!rect.width) return;
            const top = rect.top + window.scrollY;
            setHeight(Math.max(min, Math.round(window.innerHeight - top - offset)));
        };
        calc();
        window.addEventListener('resize', calc);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(calc) : null;
        if (ro) {
            ro.observe(el);
            if (el.parentElement) ro.observe(el.parentElement);
        }
        return () => {
            window.removeEventListener('resize', calc);
            if (ro) ro.disconnect();
        };
    }, [offset, min]);
    return [ref, height];
};

/** Диапазон месяцев [с, по] из одного месяца или пары. */
export const monthRangeOf = p => {
    const [a, b] = Array.isArray(p) ? p : [p, p];
    return [a.startOf('month'), (b || a).endOf('month')];
};
export const hasRole = (me, ...roles) => roles.some(r => (me?.roles || []).includes(r));

export const AUDIT_ENTITIES = {
    act: 'Акт',
    executor: 'Исполнитель',
    subcontractor: 'Субподрядчик',
    coefficient: 'Коэффициент',
    abbreviation: 'Сокращение',
    settings: 'Настройки',
    import: 'Импорт',
    role: 'Роли'
};
export const AUDIT_ACTIONS = {
    create: 'Создание',
    update: 'Изменение',
    bind: 'Привязка к субподрядчику',
    sync: 'Синхронизация',
    status: 'Смена статуса',
    payment: 'Оплата',
    item_remove: 'Строка удалена',
    item_update: 'Строка изменена',
    delete_draft: 'Черновик удалён',
    correction: 'Корректировка',
    export_pdf: 'Выгрузка PDF',
    export_xlsx: 'Выгрузка Excel',
    set: 'Установка',
    change: 'Изменение коэффициента',
    upload: 'Загрузка файла',
    deactivate: 'Отключение'
};

export const asObj = value => {
    if (!value) return {};
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value) || {};
    } catch {
        return {};
    }
};

export const formatChange = value => {
    if (value === null || value === undefined) return '';
    let obj = value;
    if (typeof value === 'string') {
        try {
            obj = JSON.parse(value);
        } catch {
            return value;
        }
    }
    if (typeof obj !== 'object') return String(obj);
    return Object.entries(obj)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v === null ? '—' : typeof v === 'object' ? JSON.stringify(v) : v}`)
        .join('; ');
};
